import type { OverviewTodo } from '@hemmafixa/shared';
import { ActivityList, Button, TodoList, type TodoItem } from '@hemmafixa/ui';
import {
  ArrowRight,
  BadgeCheck,
  ClipboardCheck,
  Flag,
  Handshake,
  HandCoins,
  MessageCircleQuestion,
  MessageSquare,
  Search,
  Trophy,
} from 'lucide-react';
import { Link } from 'react-router';
import type { Area } from '@/app/navigation';
import { fill, useI18n } from '@/i18n/context';
import { useNotifications } from '../notifications/api';
import { useDescribeNotification } from '../notifications/describe';
import { linkTo } from './link-to';

/** The API's to-dos as sentences with one action each (P5-07). */
export function OverviewTodos({
  todos,
  area,
  loading,
}: {
  todos: OverviewTodo[];
  area: Area;
  loading: boolean;
}) {
  const { t } = useI18n();
  const o = t.overview;
  const items = todos.map((todo, index): TodoItem => {
    const id = `${todo.kind}-${index}`;
    switch (todo.kind) {
      case 'compare_proposals':
        return {
          id,
          icon: HandCoins,
          title: fill(o.compareProposals, { count: todo.count, title: todo.title }),
          detail: o.compareProposalsText,
          actionLabel: o.compare,
          link: linkTo(`/client/jobs/${todo.jobId}`),
          urgent: true,
        };
      case 'choose_provider':
        return {
          id,
          icon: Trophy,
          title: fill(o.chooseProvider, { title: todo.title }),
          detail: fill(o.chooseProviderText, { names: todo.providerNames.join(', ') }),
          actionLabel: o.choose,
          link: linkTo(`/client/jobs/${todo.jobId}`),
        };
      case 'answer_questions':
        return {
          id,
          icon: MessageCircleQuestion,
          title: fill(o.answerQuestions, { title: todo.title }),
          detail: o.answerQuestionsText,
          actionLabel: o.answer,
          link: linkTo(`/client/jobs/${todo.jobId}`),
          urgent: true,
        };
      case 'unread_messages':
        return {
          id,
          icon: MessageSquare,
          title: fill(o.unreadMessages, { count: todo.count, name: todo.name }),
          detail: todo.title,
          actionLabel: o.openChat,
          link: linkTo(`/${area}/messages?c=${todo.conversationId}`),
          urgent: true,
        };
      case 'talk_to_client':
        return {
          id,
          icon: Handshake,
          title: fill(o.talkToClient, { client: todo.clientName, title: todo.title }),
          detail: o.talkToClientText,
          actionLabel: o.openChat,
          link: linkTo(`/provider/messages?c=${todo.conversationId}`),
        };
      case 'new_jobs':
        return {
          id,
          icon: Search,
          title: fill(o.newJobs, { count: todo.count }),
          detail: o.newJobsText,
          actionLabel: o.findJobs,
          link: linkTo('/provider/jobs?proposal=none'),
        };
      case 'review_jobs':
        return {
          id,
          icon: ClipboardCheck,
          count: todo.count,
          title: t.admin.overview.jobsToReview,
          actionLabel: t.dashboard.openQueue,
          link: linkTo('/admin/review'),
          urgent: true,
        };
      case 'verify_providers':
        return {
          id,
          icon: BadgeCheck,
          count: todo.count,
          title: t.admin.overview.providersToVerify,
          actionLabel: t.admin.providers.verify,
          link: linkTo('/admin/providers'),
          urgent: true,
        };
      case 'handle_reports':
        return {
          id,
          icon: Flag,
          count: todo.count,
          title: t.admin.overview.openReports,
          actionLabel: o.handle,
          link: linkTo('/admin/reports'),
          urgent: true,
        };
    }
  });
  return <TodoList title={o.needsAttention} items={items} loading={loading} empty={o.nothingWaiting} />;
}

/** The user's latest notifications as a timeline */
export function RecentActivity({ area }: { area: Area }) {
  const { t, relative } = useI18n();
  const describe = useDescribeNotification();
  const query = useNotifications({ pageSize: 6 });
  const items = (query.data?.items ?? []).map((item) => {
    const view = describe(item);
    return {
      id: item.id,
      time: relative(item.createdAt),
      title: view.title,
      detail: view.detail,
      fresh: item.readAt === null,
      link: linkTo(view.href),
    };
  });
  return (
    <ActivityList
      title={t.overview.recentActivity}
      items={items}
      loading={query.isPending}
      empty={t.overview.noActivity}
      action={
        <Button asChild variant="more" size="sm">
          <Link to={`/${area}/notifications`}>
            {t.common.viewAll} <ArrowRight />
          </Link>
        </Button>
      }
    />
  );
}
